import React, { useContext } from 'react';
import { Link } from 'gatsby';
import { addLinkTestId } from '../../helpers/addTestId';
import uniqueId from '../../helpers/uniqueId';
import { Heading } from '@legacy-vfuk/core-heading';
import { Link as ContentfulLink } from './types/Navigation.types';
import {
  CloseButtonContainer,
  MainNavigationItem,
  MainNavigationContainer,
  DrawerContainer,
} from './styles/DrawerNavigation.styles';
import { IconButton } from '@legacy-vfuk/core-icon-button';
import {
  addSlashIfMissing,
  onChangeLanguage,
} from '../../helpers/navigationHelpers';
import { LinkWithIcon } from '@legacy-vfuk/core-link-with-icon';
import { DirectionContext } from '../../contexts/DirectionContext';
import Drawer from '../Drawer/Drawer';

type DrawerNavigation = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getCurrentlyActiveLink: (
    mainLinks: ContentfulLink[]
  ) => ContentfulLink | undefined;
  mainLinks: ContentfulLink[];
  topLevelLinks: ContentfulLink[];
  languageLink: ContentfulLink;
  pageTitle: string;
  secondLanguageUrlPrefix: string | undefined;
  isSecondLanguageOnUrl: boolean | undefined;
  pageBuilderSlugList: string[];
};

const DrawerNavigation = ({
  isOpen,
  setIsOpen,
  getCurrentlyActiveLink,
  mainLinks,
  topLevelLinks,
  languageLink,
  pageTitle,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl,
  pageBuilderSlugList,
}: DrawerNavigation) => {
  const direction = useContext(DirectionContext);

  return (
    <DrawerContainer>
      <Drawer isOpen={isOpen} position="left" setIsOpen={setIsOpen}>
        <MainNavigationContainer position="left">
          <ul className="main-nav-items">
            {mainLinks.map(mainLink => (
              <MainNavigationItem
                isActive={
                  getCurrentlyActiveLink(mainLinks)?.contentful_id ===
                  mainLink.contentful_id
                }
                key={mainLink.id}
              >
                <Link
                  to={addSlashIfMissing(mainLink.url, {
                    missingFrom: 'start',
                  })}
                  id={mainLink.contentful_id}
                  title={mainLink.name}
                  data-analytics-element={uniqueId(mainLink.name)}
                  data-analytics-context={pageTitle}
                  data-analytics-category="Link"
                  data-testid={addLinkTestId('navLink', mainLink.name)}
                >
                  <Heading size={2}>{mainLink.name}</Heading>
                </Link>
              </MainNavigationItem>
            ))}
          </ul>

          <ul className="top-bar-nav-items">
            {topLevelLinks.map(topLevelLink => (
              <li key={topLevelLink.id}>
                <LinkWithIcon
                  href={topLevelLink.url}
                  icon={{
                    name:
                      direction === 'ltr' ? 'chevron-right' : 'chevron-left',
                  }}
                  text={topLevelLink.name}
                  appearance="secondary"
                  size={3}
                  dataAttributes={{
                    title: topLevelLink.name,
                    'data-analytics-element': uniqueId(topLevelLink.name),
                    'data-analytics-context': pageTitle,
                    'data-analytics-category': 'Link',
                    'data-testid': addLinkTestId(
                      'topLevelLink',
                      topLevelLink.name
                    ),
                  }}
                />
              </li>
            ))}

            {languageLink && languageLink.name !== 'no_name' && (
              <li>
                <a
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    onChangeLanguage(
                      isSecondLanguageOnUrl,
                      secondLanguageUrlPrefix,
                      pageBuilderSlugList
                    )
                  }
                  id={languageLink.contentful_id}
                  title={languageLink.name}
                  data-analytics-element={uniqueId(languageLink.name)}
                  data-analytics-context={pageTitle}
                  data-analytics-category="Link"
                  data-testid={addLinkTestId('topLevelLink', languageLink.name)}
                >
                  {languageLink.name}
                </a>
              </li>
            )}
          </ul>
        </MainNavigationContainer>
        <CloseButtonContainer>
          <IconButton
            icon={{ name: 'close' }}
            srText="close-drawer"
            appearance="secondary"
            size={4}
            onClick={() => setIsOpen(false)}
          />
        </CloseButtonContainer>
      </Drawer>
    </DrawerContainer>
  );
};

export default DrawerNavigation;
